import {
  BlockPlaybook,
  EndOfConversationType,
  PlaybookActionActionType,
  PlaybookStatus,
} from '@driftt/api-types'
import QueryString from 'qs'

import { ConversationAPI, ReuseApi } from 'api'
import { PlaybookBlockData } from 'components/BlockEditorNavigation'

export interface BlockUpdateVersionData {
  blockId: string
  name: string
  currentVersionNumber: string
  latestVersionNumber: string
  addedOutputKeys: string[]
  removedOutputKeys: string[]
  updatedOutputKeys: string[]
  currentBlockOutputConfiguration: {
    [index: string]: {
      label: string
      endOfConversationType: EndOfConversationType
    }
  }
  latestBlockOutputConfiguration: {
    [index: string]: {
      label: string
      endOfConversationType: EndOfConversationType
    }
  }
}

export interface BlockTableRow {
  blockId: string
  blockName: string
  createdBy: number
  createdAt: number
  updatedBy: number
  updatedAt: number
  playbookBlockData: PlaybookBlockData[]
}

interface BlockTableResponse {
  blockPageRows: BlockTableRow[]
  meta: {
    page: number
    totalPages: number
  }
}

export interface BlocksTableQueryParams {
  status?: PlaybookStatus
  nameQuery?: string
  sortProperty?: string
  sortDirection?: 'asc' | 'desc'
  page?: number
  pageSize?: number
}

interface FileUploadProps {
  fileUrl: string | null
  fileName: string | null
}

export const fetchPlaybookFormattedBlock = (id: string) =>
  ReuseApi.get(`/blocksV2/playbook/${id}`)

export const savePlaybookFormattedBlock = (block?: BlockPlaybook) =>
  ReuseApi.post<BlockPlaybook>(`/blocksV2/playbook`, block)

export const getConsumingPlaybooks = (blockId: string) =>
  ReuseApi.get(`/blocksV2/activePlaybooks/${blockId}`)

export const getLatestBlockChanges = (blockId: string) =>
  ReuseApi.get<BlockUpdateVersionData>(
    `/blocksV2/version/latestChanges/${blockId}`
  )

export const getPlaybookBlocks = () =>
  ReuseApi.get<BlockPlaybook[]>(
    `/blocksV2/version/organization/all?status=ENABLED`
  )

export const getArchivedBlocksUsedInPlaybook = (playbookId: string) =>
  ReuseApi.get<{ [key: string]: string }>(
    `/blocksV2/blocks_by_status/used_in/${playbookId}`,
    {
      params: {
        status: PlaybookStatus.ARCHIVED,
      },
    }
  )

export const getBlocksTableData = (params: BlocksTableQueryParams) => {
  const query = QueryString.stringify(params, { arrayFormat: 'repeat' })
  return ReuseApi.get<BlockTableResponse>(
    `blocksV2/version/organization/all/v2?${query}`
  )
}

export const cloneBlock = (blockId: string) =>
  ReuseApi.post(`/blocksV2/version/${blockId}/clone`)

export const archiveBlock = (blockId: string) =>
  ReuseApi.patch(`/blocksV2/version/${blockId}/ARCHIVED`)

export const deleteBlock = (blockId: string) =>
  ReuseApi.patch(`/blocksV2/version/${blockId}/DELETED`)

export const unarchiveBlock = (blockId: string) =>
  ReuseApi.patch(`/blocksV2/version/${blockId}/ENABLED`)

export const updatePlaybookName = (blockId: string, name: string) =>
  ReuseApi.patch(`/blocksV2/version/${blockId}/name`, name)

const getNewBlockPayload = (userId: number, orgId: number) => {
  const blockName = 'New block'
  return {
    createdAuthorId: userId,
    updatedAuthorId: userId,
    id: 0,
    orgId: orgId,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    name: blockName,
    actions: [
      {
        action: {
          orgId: orgId,
          slug: '',
          createdAt: Date.now(),
          updatedAt: Date.now(),
          createdBy: userId,
          lastUpdatedBy: userId,
          endOfConversationConfig: {
            endOfConversationType: 'PERMACLOSE',
            messageText: '<p>permaclose message at end of conversation.</p>',
            label: null,
          },
          name: blockName,
          greeting: '',
          playbookId: 0,
          playbookVersion: null,
          status: 'ENABLED',
          questions: [],
          skills: [],
          displayOrder: [],
          startingNodeId: null,
        },
        actionType: PlaybookActionActionType.INTERACTION,
      },
    ],
    actionGroups: [],
    meta: {},
  }
}

export const uploadBlockTranslation = (
  blockId: string | null,
  file: FileUploadProps,
  overwrite: boolean = false,
  blockName: string
) => {
  return ConversationAPI.post('/playbook_copy/import', {
    overwrite,
    blockId,
    file,
    playbookName: blockName,
  })
}

export const createPlaybookFormattedBlock = (userId: number, orgId: number) => {
  return savePlaybookFormattedBlock(getNewBlockPayload(userId, orgId))
}

export const exportBlockCopy = (blockId: string) =>
  ConversationAPI.get<string>(`/playbook_copy/export?blockId=${blockId}`)
